import { useContext } from 'react';
import styled, { ThemeContext } from 'styled-components';
import PropTypes from 'prop-types';
import textHelper from '@utils/textHelper';
import Remove from '@icon/Remove';
import Add from '@icon/Add';
import Paper from '@base/Paper/Paper';
import P1 from '@base/P1/P1';
import P2 from '@base/P2/P2';
import Tags from '@base/Tags/Tags';
import boxShadowGenerator from '@utils/boxShadowGenerator';
import {
  BorderColors,
  Spacings,
  PRIMARY_BORDER_WIDTH,
  SOLID_BORDER_STYLE,
  TextColors
} from '@styles/index';

const GoalCardContainer = styled(Paper)`
  grid-column: ${({ isMyGoal }) => (isMyGoal ? 'span 9' : 'span 6')};
  position: relative;
  padding: ${Spacings.SPACING_6B};
  background: ${({ theme }) => theme.colors.white};
  margin-top: ${Spacings.SPACING_4B};
  border-radius: 8px;
  display: ${({ isMyGoal }) => (isMyGoal ? 'flex' : 'block')};
  align-items: center;
  cursor: pointer;
  :hover {
    box-shadow: ${({ theme }) =>
      boxShadowGenerator({
        yOffset: Spacings.SPACING_1B,
        blur: Spacings.SPACING_2B,
        theme
      })};
  }
  transition: box-shadow 0.2s ease-in-out;
  @media (max-width: 70.95em) {
    grid-column: span 9;
    display: block;
  }
  @media (max-width: 47.95em) {
    display: flex;
    align-items: center;
  }
`;

const GoalName = styled(P1)`
  color: ${({ theme }) => theme.colors.fontGrey};
  @media (max-width: 47.95em) {
    max-width: 170px;
  }
`;

const GoalIcon = styled.img`
  height: 40px;
  width: 40px;
  margin-right: ${({ isMyGoal }) => (isMyGoal ? Spacings.SPACING_5B : 0)};
  margin-bottom: ${({ isMyGoal }) => (isMyGoal ? 0 : Spacings.SPACING_5B)};
  @media (max-width: 47.95em) {
    margin-bottom: 0;
    margin-right: 12px;
  }
`;

const ActionButton = styled.div`
  position: absolute;
  height: 25px;
  width: 25px;
  border-radius: 4px;
  border-width: ${PRIMARY_BORDER_WIDTH};
  border-style: ${SOLID_BORDER_STYLE};
  border-color: ${({ isMyGoal, isFollowedGoal, theme }) =>
    isMyGoal || isFollowedGoal ? BorderColors.RED : theme.colors.fontGrey};
  top: ${({ isMyGoal }) => (isMyGoal ? '33%' : Spacings.SPACING_4B)};
  right: ${Spacings.SPACING_4B};
  padding: ${Spacings.SPACING_1B};
`;

const SubscribedTag = styled(Tags)`
  position: absolute;
  right: ${Spacings.SPACING_4B};
  top: ${({ isMyGoal }) => (isMyGoal ? '33%' : Spacings.SPACING_4B)};
`;

const CoursesLength = styled(P2)`
  margin-top: ${Spacings.SPACING_1B};
`;

const GoalCard = ({
  goal,
  isMyGoal,
  isSubscribedGoal,
  isFollowedGoal,
  removeGoalAction,
  onClick
}) => {
  const theme = useContext(ThemeContext);
  const renderActionButton = () => {
    if (isSubscribedGoal) {
      return (
        <SubscribedTag isMyGoal={isMyGoal} type="hollow">
          Subscribed
        </SubscribedTag>
      );
    }
    if (isFollowedGoal) {
      return (
        <ActionButton
          isMyGoal={isMyGoal}
          isFollowedGoal={isFollowedGoal}
          onClick={(e) => {
            e.stopPropagation();
            removeGoalAction(goal);
          }}
        >
          <Remove size={Spacings.SPACING_4B} color={TextColors.RED} />
        </ActionButton>
      );
    }

    return (
      <ActionButton>
        <Add size={Spacings.SPACING_4B} color={theme.colors.fontGrey} />
      </ActionButton>
    );
  };

  return (
    <GoalCardContainer
      key={`goal-${goal.uid}`}
      isMyGoal={isMyGoal}
      onClick={() => onClick(goal)}
    >
      <GoalIcon src={goal.icon} isMyGoal={isMyGoal} alt={goal.name} />
      <div>
        <GoalName>{goal.name}</GoalName>
        {!isMyGoal ? (
          <CoursesLength color={theme.colors.fontLightGrey}>
            {textHelper.pluralize(goal.courses_count, 'course')}
          </CoursesLength>
        ) : null}
        {renderActionButton()}
      </div>
    </GoalCardContainer>
  );
};

export default GoalCard;

GoalCard.propTypes = {
  goal: PropTypes.objectOf({
    icon: PropTypes.string,
    name: PropTypes.string,
    courses_count: PropTypes.number
  }).isRequired,
  removeGoalAction: PropTypes.func.isRequired,
  isMyGoal: PropTypes.bool.isRequired,
  isSubscribedGoal: PropTypes.bool.isRequired,
  isFollowedGoal: PropTypes.bool.isRequired,
  onClick: PropTypes.func.isRequired
};
